import React, { useEffect, useState } from 'react';
import { Button, Input, message, Modal, Form, Upload, Card, List, Popconfirm, Spin, Switch } from 'antd';
import { PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined, HeartOutlined, HeartFilled } from '@ant-design/icons';
import drEasyApi from '../../../Api/Customapi';
import ImageUploader from '../../Ui/ImageUploader';
import { getImg } from '../../../Api/getImg';
import { MdHomeRepairService } from 'react-icons/md';

const ServicesScreen = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [favoriteLoading, setFavoriteLoading] = useState(new Set());
    const [form] = Form.useForm();

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        setLoading(true);
        try {
            const res = await drEasyApi.get('/api/Services/GetMyServices');
            setServices(res.data || []);
        } catch (err) {
            if (err.response.status == 403) {
                window.location.href = "/sub"; // Adjust auth path as needed
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingService(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (service) => {
        setEditingService(service);
        // API returns titleEn, titleAr, descriptionEn, descriptionAr
        form.setFieldsValue({
            titleEn: service.titleEn,
            titleAr: service.titleAr,
            descriptionEn: service.descriptionEn,
            descriptionAr: service.descriptionAr,
            photoID: service.photoID,
            price: service.price
        });
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await drEasyApi.delete(`/api/Services/Delete`, { params: { id } });
            message.success('Service deleted');
            fetchServices();
        } catch (err) {
            message.error('Failed to delete service');
        }
    };

    const handleToggleFavorite = async (serviceId) => {
        const service = services.find(s => s.id === serviceId);
        const currentFavoriteCount = services.filter(s => s.isFavorite).length;
        
        // Check if trying to add favorite when already at limit
        if (!service.isFavorite && currentFavoriteCount >= 6) {
            message.warning('You can only have a maximum of 4 favorite services');
            return;
        }

        // Add service ID to loading set
        setFavoriteLoading(prev => new Set([...prev, serviceId]));
        
        try {
            await drEasyApi.put('/api/Services/toggleFavorite', {
                serviceId: serviceId
            });
            
            // Update the service's favorite status in local state
            setServices(prevServices => 
                prevServices.map(service => 
                    service.id === serviceId 
                        ? { ...service, isFavorite: !service.isFavorite }
                        : service
                )
            );
            
            const actionMessage = service.isFavorite ? 'Removed from favorites' : 'Added to favorites';
            message.success(actionMessage);
        } catch (err) {
            message.error('Failed to update favorite status');
        } finally {
            // Remove service ID from loading set
            setFavoriteLoading(prev => {
                const newSet = new Set(prev);
                newSet.delete(serviceId);
                return newSet;
            });
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // Set price to null if not provided
            if (!values.price || values.price === '') {
                values.price = null;
            }

            if (editingService) {
                // Map form values to API expected format for edit
                const editPayload = {
                    id: editingService.id,
                    title: values.titleEn,
                    titleAr: values.titleAr,
                    description: values.descriptionEn,
                    descriptionAr: values.descriptionAr,
                    photoID: values.photoID,
                    price: values.price
                };
                await drEasyApi.put('/api/Services/Edit', editPayload);
                message.success('Service updated');
            } else {
                await drEasyApi.post('/api/Services/Add', values);
                message.success('Service added');
            }
            fetchServices();
            setIsModalVisible(false);
        } catch (err) {
            if (err?.response?.status === 400) {
                const errorMessage = err?.response?.data?.message || 'Please check your input and try again';
                message.error(errorMessage);
            } else if (err?.errorFields) {
                // Form validation error
                message.error('Please fill all required fields');
            } else {
                message.error('Failed to submit service');
            }
        }
    };
    
    const handleToggleActive = async (serviceId, isActive) => {
        try {
            await drEasyApi.put('/api/Services/toogleActive', {
                serviceId: serviceId,
                isActive: isActive
            });
            // Update the service's active status in local state
            setServices(prevServices =>
                prevServices.map(service =>
                    service.id === serviceId
                        ? { ...service, isActive: isActive }
                        : service
                )
            );
            message.success(`Service ${isActive ? 'activated' : 'deactivated'}`);
        } catch (err) {
            message.error('Failed to update service status');
        }
    };

    // Get current favorite count for display
    const favoriteCount = services.filter(service => service.isFavorite).length;

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h2 className="text-xl font-semibold">My Services</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Favorites: {favoriteCount}/4 (You can mark up to 4 services as favorites)
                    </p>
                </div>
                <Button type="primary" className='bg-[#298d9f]' icon={<PlusOutlined />} onClick={handleAdd}>Add Service</Button>
            </div>

            {loading ? (
                <div className="flex justify-center items-center">
                    <Spin size="large" />
                </div>
            ) : (
                <List
                    grid={{
                        gutter: 16,
                        xs: 1,
                        sm: 2,
                        md: 2,
                        lg: 3,
                        xl: 4,
                        xxl: 4,
                    }}
                    dataSource={services}
                    renderItem={(item) => {
                        const currentFavoriteCount = services.filter(s => s.isFavorite).length;
                        const canToggleFavorite = item.isFavorite || currentFavoriteCount < 6;
                        
                        return (
                            <List.Item>
                                <Card
                                    className="overflow-hidden shadow-md border border-[#298d9f73] hover:shadow-lg transition-all rounded-lg relative"
                                    cover={
                                        item.photoID ? (
                                            <div className="bg-[#f6fcfd] p-2 flex justify-center items-center h-60 relative">
                                                <img
                                                    src={getImg(item.photoID)}
                                                    alt="Service"
                                                className="max-h-full w-full object-contain rounded"
                                                />
                                                {/* Heart icon in top-right corner of image */}
                                                <Button
                                                    type="text"
                                                    className="absolute top-2 right-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-1 shadow-sm"
                                                    icon={
                                                        favoriteLoading.has(item.id) ? (
                                                            <Spin size="small" />
                                                        ) : item.isFavorite ? (
                                                            <HeartFilled style={{ color: '#ff4d4f', fontSize: '18px' }} />
                                                        ) : (
                                                            <HeartOutlined style={{ 
                                                                color: canToggleFavorite ? '#666' : '#ccc', 
                                                                fontSize: '18px' 
                                                            }} />
                                                        )
                                                    }
                                                    onClick={() => handleToggleFavorite(item.id)}
                                                    loading={favoriteLoading.has(item.id)}
                                                    disabled={!canToggleFavorite}
                                                    title={!canToggleFavorite ? 'Maximum 4 favorites allowed' : ''}
                                                />
                                            </div>
                                        ) : (
                                            // Heart icon for cards without images
                                            <div className="bg-[#f6fcfd] p-2 h-16 flex justify-end items-start">
                                                <Button
                                                    type="text"
                                                    className="bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-1 shadow-sm"
                                                    icon={
                                                        favoriteLoading.has(item.id) ? (
                                                            <Spin size="small" />
                                                        ) : item.isFavorite ? (
                                                            <HeartFilled style={{ color: '#ff4d4f', fontSize: '18px' }} />
                                                        ) : (
                                                            <HeartOutlined style={{ 
                                                                color: canToggleFavorite ? '#666' : '#ccc', 
                                                                fontSize: '18px' 
                                                            }} />
                                                        )
                                                    }
                                                    onClick={() => handleToggleFavorite(item.id)}
                                                    loading={favoriteLoading.has(item.id)}
                                                    disabled={!canToggleFavorite}
                                                    title={!canToggleFavorite ? 'Maximum 4 favorites allowed' : ''}
                                                />
                                            </div>
                                        )
                                    }
                                    actions={[
                                        <Button
                                            icon={<EditOutlined />}
                                            onClick={() => handleEdit(item)}
                                            type="text"
                                            key="edit"
                                        />,
                                        <Popconfirm
                                            title="Are you sure to delete this service?"
                                            onConfirm={() => handleDelete(item.id)}
                                            key="delete"
                                        >
                                            <Button danger icon={<DeleteOutlined />} type="text" />
                                        </Popconfirm>,
                                    ]}
                                >
                                    <Card.Meta
                                        title={
                                            <div className="flex justify-between items-center gap-2 text-lg font-semibold text-[#298d9f]">
                                                <p className='flex items-center gap-1'>

                                                <MdHomeRepairService />
                                                {item.titleEn} 
                                                </p>
                                                <Switch style={{backgroundColor: item.isActive ? '#46a049ff' : '#9e9595ff'}} checked={item.isActive} onChange={(checked) => handleToggleActive(item.id, checked)} />
                                            </div>
                                        }
                                        description={
                                            <>
                                                <p className="text-sm text-gray-600">{item.descriptionEn}</p>
                                                {item.price !== null && item.price !== undefined && (
                                                    <p className="mt-3 text-base font-semibold text-[#298d9f] flex items-center gap-1">
                                                      {item.price}  <span className='font-bold '> EGP</span> 
                                                    </p>
                                                )}
                                            </>
                                        }
                                    />
                                </Card>
                            </List.Item>
                        );
                    }}
                />
            )}

            <Modal
                title={editingService ? 'Edit Service' : 'Add Service'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSubmit}
                        style={{ backgroundColor: '#298d9f', borderColor: '#298d9f' }}
                    >
                        Save
                    </Button>,
                ]}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="titleEn" label="Title" rules={[{ required: true, message: 'Please enter title' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="titleAr" label="Title (In Arabic)" rules={[{ required: true, message: 'Please enter title' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="descriptionEn" label="Description" rules={[{ required: true, message: 'Please enter description' }]}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="descriptionAr" label="Description (In Arabic)" rules={[{ required: true, message: 'Please enter description' }]}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item
                        name="price"
                        label="Price (Optional)"
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value || value === '') {
                                        return Promise.resolve();
                                    }
                                    if (parseFloat(value) < 0) {
                                        return Promise.reject(new Error('Price cannot be negative'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Enter price (optional)"
                            onKeyDown={(e) => {
                                // Prevent minus key input
                                if (e.key == '-' || e.key == 'e' || e.key == 'E') {
                                    e.preventDefault()
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item name="photoID" label="Service Image" rules={[{ required: true, message: 'Please add image' }]}>
                        <ImageUploader form={form} value={form.getFieldValue("photoID")} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ServicesScreen;